import type { PresentationData } from "./types";

// Mock agents. Note: agent data is fully data-driven — later PitchAI will
// receive name, brokerage, photo, phone, email and scheduling URL from LeadOS.
const alain = {
  name: "Alain Onesti",
  title: "Licensed Real Estate Salesperson",
  team: "Heather Domi Team",
  officeAddress: "575 Madison Avenue, New York, NY 10022",
  brokerage: "Douglas Elliman",
  phone: "+16468993079",
  email: "alain@heatherdomi.com",
  schedulingUrl: "https://calendly.com/aonesti-elliman",
  photoUrl: "/agents/alain-onesti.jpg",
};

const elena = {
  name: "Elena Marchetti",
  title: "Licensed Associate Real Estate Broker",
  brokerage: "Compass",
  phone: "+12125552290",
  email: "elena.marchetti@compass.com",
  schedulingUrl: "https://calendly.com/elena-marchetti/intro",
  photoUrl:
    "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHJlYWx0b3IlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NjM4NzQ2M3ww&ixlib=rb-4.1.0&q=85",
};

// ---------------------------------------------------------------------------
// Dataset 1 — COMPLETE DATA
// ---------------------------------------------------------------------------
const sellerRelaunchComplete: PresentationData = {
  slug: "7fk29q",
  templateType: "seller_relaunch",
  noindex: true,
  owner: { firstName: "John", fullName: "John Mercer" },
  agent: alain,
  property: {
    address: "530 East 90th Street",
    unit: "4B",
    neighborhood: "Yorkville",
    city: "Manhattan",
    currentAskingPrice: 1395000,
    originalAskingPrice: 1550000,
    bedrooms: 2,
    bathrooms: 2,
    squareFeet: 1180,
    daysOnMarket: 140,
    listingStatus: "withdrawn",
    images: [
      {
        url: "https://images.unsplash.com/photo-1773754532196-014342510e64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBuZXclMjB5b3JrJTIwYXBhcnRtZW50JTIwaW50ZXJpb3IlMjBsaXZpbmclMjByb29tfGVufDB8fHx8MTc4NjM4NzM0MXww&ixlib=rb-4.1.0&q=85",
        alt: "Sunlit living room at 530 East 90th Street with large windows",
        caption: "The corner living room, mid-morning",
      },
      {
        url: "https://images.pexels.com/photos/35189708/pexels-photo-35189708.jpeg?auto=compress&cs=tinysrgb&w=1400",
        alt: "Modern kitchen with marble surfaces",
        caption: "Kitchen",
      },
      {
        url: "https://images.unsplash.com/photo-1616594092403-fb65629b0a46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwYmVkcm9vbSUyMGludGVyaW9yJTIwbmF0dXJhbCUyMGxpZ2h0fGVufDB8fHx8MTc4NjM4NzM1MXww&ixlib=rb-4.1.0&q=85",
        alt: "Primary bedroom with soft natural light",
        caption: "Primary bedroom",
      },
      {
        url: "https://images.unsplash.com/photo-1512621480870-77463b1b90c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxuZXclMjB5b3JrJTIwYnVpbGRpbmclMjBmYWNhZGV8ZW58MHx8fHwxNzg1OTk0MTE4fDA&ixlib=rb-4.1.0&q=85",
        alt: "Building facade in Yorkville",
        caption: "The building",
      },
    ],
  },
  hero: {
    greetingLine: "John, I took a closer look at your place.",
    body: "530 East 90th Street has a lot going for it. I went back through how it was presented the first time around and found a few things that I think could make a real difference next time.",
    followUp: "So I put together a short relaunch plan for you.",
    scrollCue: "Here's what I found",
  },
  snapshot: {
    observation: "There's a lot to work with here.",
    strengths: [
      "A quiet, light-filled corner layout on a stretch of Yorkville that buyers keep coming back to. The bones are genuinely good — this is a home that shows better in person than it did on screen.",
    ],
  },
  analysis: {
    overallObservation:
      "Nothing here points to a problem with the apartment. It points to how the market first met it.",
    observations: [
      {
        title: "The opening price set the tone",
        observation:
          "The first few weeks of a listing are when the most motivated buyers are paying attention. At $1.55M, that early audience may have quietly filed it as a touch ahead of the market and moved on.",
        evidence: "$1.55M → $1.49M after 31 days",
      },
      {
        title: "The best rooms weren't leading",
        observation:
          "The corner living room and the light are the strongest things about this apartment, but they weren't the first thing buyers saw online. The lead photo didn't do the place justice.",
        evidence: "Lead image was the building lobby",
      },
      {
        title: "The layout was underexplained",
        observation:
          "A true split two-bedroom is rarer than it sounds at this price. That wasn't called out clearly, so I suspect a lot of buyers scrolled past without realizing what they were looking at.",
      },
      {
        title: "Momentum faded before the last cut",
        observation:
          "By the second adjustment, the listing had been out long enough that it read as still available rather than new. A lower number on its own couldn't have reset that.",
        evidence: "140 days on market",
      },
    ],
  },
  listingHistory: {
    events: [
      { label: "Listed", price: 1550000, date: "March 12" },
      { label: "Price adjustment", price: 1490000, date: "April 12", daysSincePrevious: 31 },
      { label: "Price adjustment", price: 1395000, date: "May 24", daysSincePrevious: 42 },
      { label: "Off market", date: "July 30", daysSincePrevious: 67, isEnd: true },
    ],
    interpretation:
      "By the time the price landed where it did, most of the buyers actively looking in the neighborhood had already seen it — and passed. A relaunch should feel like a genuinely new listing, not the same one returning at a lower number.",
  },
  opportunity: {
    headline: "The opportunity is in the reset.",
    body: "A listing that didn't sell the first time usually isn't a problem with the apartment. More often it's about how the market first saw it — the price, the photos, the story. All of that can be changed.",
    pullQuote:
      "Don't just put it back on the market. Give the market a reason to look again.",
  },
  strategy: {
    intro: "Here's how I'd approach it, start to finish.",
    stages: [
      {
        key: "reposition",
        number: "01",
        title: "Reposition",
        summary: "Get the price and the story right before anyone sees it again.",
        points: [
          "Re-run the comparables from the last few months, not last year's",
          "Lead with the split two-bedroom layout and the corner light",
          "Set a price that invites offers instead of anchoring high",
        ],
      },
      {
        key: "repackage",
        number: "02",
        title: "Repackage",
        summary: "Make the first impression match what the apartment actually feels like.",
        points: [
          "New photography, shot for the morning light",
          "Lead with the living room, not the lobby",
          "Rewrite the description around how the space really lives",
          "Add a clean floor plan so the layout reads instantly",
        ],
      },
      {
        key: "relaunch",
        number: "03",
        title: "Relaunch",
        summary: "Bring it back as a new listing, with real momentum behind it.",
        points: [
          "Coordinate a single, concentrated launch week",
          "A brokerage-wide preview before it hits the public sites",
          "Targeted outreach to agents with active buyers in Yorkville",
        ],
      },
      {
        key: "re_engage",
        number: "04",
        title: "Re-engage",
        summary: "Go back to the people who were already close.",
        points: [
          "Quietly re-approach buyers who saw it the first time",
          "Reach agents whose clients looked at comparable two-bedrooms",
          "Keep a tight feedback loop through the first two weeks",
        ],
      },
    ],
  },
  launchPlan: {
    intro:
      "The first two weeks carry more weight than most sellers expect. I'd want them planned, not improvised.",
    phases: [
      { label: "Preparation", detail: "Photography, copy, floor plan and pricing finalized before anything goes live." },
      { label: "Private Preview", detail: "A quiet first look for a select group of agents and their buyers." },
      { label: "Launch", detail: "The public listing goes live across every channel on the same day." },
      { label: "Week 1", detail: "Concentrated showings and open houses while attention is highest." },
      { label: "Week 2 Review", detail: "We sit down, read the real feedback, and adjust with intent." },
    ],
  },
  recommendation: {
    body: "If this were my listing, I wouldn't just switch the old marketing back on. I'd use the time off-market to reset the price, reshoot it properly, and bring it back as something that feels new. Most of the work is in the first impression — and that's the part we can still control.",
    closing: "Happy to walk you through the details whenever you have twenty minutes.",
  },
  contact: {
    headline: "Want to talk through it?",
    body: "I'd be glad to walk you through what I saw and how I'd handle the relaunch. No obligation — even a short conversation might give you a useful second read on things.",
  },
};

// ---------------------------------------------------------------------------
// Dataset 2 — SOME MISSING DATA (no listing history, missing sqft/original
// price, only two images, no scheduling URL)
// ---------------------------------------------------------------------------
const sellerRelaunchPartial: PresentationData = {
  slug: "a3x8m2",
  templateType: "seller_relaunch",
  noindex: true,
  owner: { firstName: "Maria" },
  agent: { ...alain, schedulingUrl: undefined },
  property: {
    address: "212 West 22nd Street",
    unit: "3R",
    neighborhood: "Chelsea",
    city: "Manhattan",
    currentAskingPrice: 985000,
    bedrooms: 1,
    bathrooms: 1,
    daysOnMarket: 88,
    listingStatus: "expired",
    images: [
      {
        url: "https://images.unsplash.com/photo-1759407519051-a4fd31fa2a45?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBuZXclMjB5b3JrJTIwYXBhcnRtZW50JTIwaW50ZXJpb3IlMjBsaXZpbmclMjByb29tfGVufDB8fHx8MTc4NjM4NzM0MXww&ixlib=rb-4.1.0&q=85",
        alt: "Living room with striped sofa in a Chelsea apartment",
      },
      {
        url: "https://images.unsplash.com/photo-1754999809963-79a41e8fb648?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBuZXclMjB5b3JrJTIwYXBhcnRtZW50JTIwaW50ZXJpb3IlMjBsaXZpbmclMjByb29tfGVufDB8fHx8MTc4NjM4NzM0MXww&ixlib=rb-4.1.0&q=85",
        alt: "Cozy living space with elegant furniture",
      },
    ],
  },
  hero: {
    greetingLine: "Maria, I spent some time with your listing.",
    body: "212 West 22nd Street is a genuinely nice one-bedroom in a part of Chelsea people want to be in. Looking back at how it was marketed, I think a few small changes could shift how buyers respond to it.",
    followUp: "Here's a short plan for bringing it back.",
    scrollCue: "Take a look",
  },
  snapshot: {
    observation: "This one shows well.",
    strengths: [
      "A well-proportioned one-bedroom on a quiet block, close to everything that makes Chelsea Chelsea. It photographs warmly and feels bigger than the number suggests.",
    ],
  },
  analysis: {
    observations: [
      {
        title: "It read as ordinary online",
        observation:
          "Nothing about the presentation was wrong — it just didn't stand out. In a neighborhood with a lot of one-bedrooms, blending in is its own kind of problem.",
      },
      {
        title: "The light was underused",
        observation:
          "The apartment gets real afternoon light, but the photos were shot flat. That's an easy thing to get right the second time.",
      },
      {
        title: "The listing went quiet",
        observation:
          "After the first couple of weeks there wasn't much to bring buyers back. A relaunch gives us a clean, deliberate reason to get their attention again.",
        evidence: "88 days on market",
      },
    ],
  },
  opportunity: {
    headline: "A fresh start, done deliberately.",
    body: "An expired listing rarely means the apartment fell short. Usually it means the first launch didn't build enough momentum. That's very fixable.",
    pullQuote: "Give buyers a reason to look at it as if for the first time.",
  },
  strategy: {
    stages: [
      {
        key: "reposition",
        number: "01",
        title: "Reposition",
        summary: "Sharpen who this is for and price it to move.",
        points: [
          "Reconfirm the price against the most recent Chelsea sales",
          "Lead with the light, the block and the value",
        ],
      },
      {
        key: "repackage",
        number: "02",
        title: "Repackage",
        summary: "Give it a presentation that stands out.",
        points: [
          "Reshoot in the afternoon light",
          "A warmer, more specific description",
          "Light staging touches where they matter",
        ],
      },
      {
        key: "relaunch",
        number: "03",
        title: "Relaunch",
        summary: "Come back with intent, not quietly.",
        points: [
          "A focused launch week with an open house",
          "Direct outreach to agents with active buyers nearby",
        ],
      },
      {
        key: "re_engage",
        number: "04",
        title: "Re-engage",
        summary: "Reconnect with buyers who were nearly there.",
        points: [
          "Follow up with anyone who showed interest the first time",
          "Keep a close read on early feedback",
        ],
      },
    ],
  },
  launchPlan: {
    phases: [
      { label: "Preparation", detail: "Photos, copy and pricing set before we go live." },
      { label: "Launch", detail: "Live across every channel on the same day." },
      { label: "Week 1", detail: "Showings and an open house while interest is fresh." },
      { label: "Week 2 Review", detail: "Read the feedback and adjust." },
    ],
  },
  recommendation: {
    body: "If it were mine, I'd reshoot it, tighten the story and relaunch it properly rather than quietly relisting. It doesn't need much — it needs a stronger first impression.",
  },
  contact: {
    headline: "Want to talk it through?",
    body: "Happy to share what I noticed and how I'd approach it. No pressure — even a quick call might be useful.",
  },
};

// ---------------------------------------------------------------------------
// Dataset 3 — EXTENDED CONTENT + DIFFERENT AGENT (higher price, more detail)
// ---------------------------------------------------------------------------
const sellerRelaunchExtended: PresentationData = {
  slug: "k9p4w1",
  templateType: "seller_relaunch",
  noindex: true,
  owner: { firstName: "Daniel", fullName: "Daniel Rosen" },
  agent: elena,
  property: {
    address: "88 Franklin Street",
    unit: "PH2",
    neighborhood: "Tribeca",
    city: "Manhattan",
    currentAskingPrice: 6250000,
    originalAskingPrice: 6950000,
    bedrooms: 3,
    bathrooms: 3,
    squareFeet: 2650,
    daysOnMarket: 205,
    listingStatus: "withdrawn",
    images: [
      {
        url: "https://images.unsplash.com/photo-1598718544285-7180f670198b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHx3YXJtJTIwbW9kZXJuJTIwbG9mdCUyMGludGVyaW9yJTIwd29vZCUyMGZsb29yfGVufDB8fHx8MTc4NjM4NzQ2NHww&ixlib=rb-4.1.0&q=85",
        alt: "Open loft living space with wide-plank wood floors",
        caption: "The great room",
      },
      {
        url: "https://images.unsplash.com/photo-1505873242700-f289a29e1e0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHx3YXJtJTIwbW9kZXJuJTIwbG9mdCUyMGludGVyaW9yJTIwd29vZCUyMGZsb29yfGVufDB8fHx8MTc4NjM4NzQ2NHww&ixlib=rb-4.1.0&q=85",
        alt: "Sitting area with leather sofa and warm textures",
        caption: "Sitting area",
      },
      {
        url: "https://images.unsplash.com/photo-1625707695772-5dd6c1f9c107?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHx3YXJtJTIwbW9kZXJuJTIwbG9mdCUyMGludGVyaW9yJTIwd29vZCUyMGZsb29yfGVufDB8fHx8MTc4NjM4NzQ2NHww&ixlib=rb-4.1.0&q=85",
        alt: "Dining area beneath loft ceilings",
        caption: "Dining",
      },
      {
        url: "https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHx3YXJtJTIwbW9kZXJuJTIwbG9mdCUyMGludGVyaW9yJTIwd29vZCUyMGZsb29yfGVufDB8fHx8MTc4NjM4NzQ2NHww&ixlib=rb-4.1.0&q=85",
        alt: "Detail of the loft interior finishes",
        caption: "Detail",
      },
    ],
  },
  hero: {
    greetingLine: "Daniel, I studied the penthouse carefully.",
    body: "88 Franklin is a special apartment — the ceiling height, the light, the address. I went back through the whole of the last campaign, and I think the story it told didn't quite match the home itself. At this level, the story matters as much as the space.",
    followUp: "I've mapped out how I'd bring it back to the market.",
    scrollCue: "Here's my thinking",
  },
  snapshot: {
    observation: "This is a rare one.",
    strengths: [
      "A full-floor loft penthouse in prime Tribeca — the scale, the volume and the light are all things buyers at this level are genuinely searching for.",
      "Homes like this don't compete on square footage. They compete on feeling, and this one has it in person.",
    ],
  },
  analysis: {
    overallObservation:
      "At this price, buyers are few and discerning. The last campaign spoke to a wider audience than the apartment was ever going to attract — and I think that softened its impact.",
    observations: [
      {
        title: "The price led the conversation",
        observation:
          "Opening at $6.95M put the apartment above where the most credible buyers were willing to start. Once a number like that sits for a while, the later reductions can read as a signal rather than a correction.",
        evidence: "$6.95M → $6.25M over 205 days",
      },
      {
        title: "The volume didn't translate",
        observation:
          "Standing inside, the ceiling height and openness are the first things you feel. The photography flattened all of that. For a home that sells on feeling, that's the whole game.",
        evidence: "Wide-angle interiors, shot mid-day",
      },
      {
        title: "It was marketed as a three-bedroom",
        observation:
          "Leading with the bedroom count invited comparison against far more conventional apartments. This is a full-floor loft — that's a different, and smaller, pool of buyers who value it more.",
      },
      {
        title: "The address was underplayed",
        observation:
          "Franklin Street is one of the best blocks in Tribeca. That deserved to be part of the pitch from the first line, not a footnote.",
      },
      {
        title: "The campaign ran long and quiet",
        observation:
          "Over two hundred days on market at this level starts to work against you. Serious buyers notice, and it invites them to wait rather than act.",
        evidence: "205 days on market",
      },
    ],
  },
  listingHistory: {
    events: [
      { label: "Listed", price: 6950000, date: "January 8" },
      { label: "Price adjustment", price: 6650000, date: "March 2", daysSincePrevious: 53 },
      { label: "Price adjustment", price: 6250000, date: "May 20", daysSincePrevious: 79 },
      { label: "Withdrawn", date: "July 31", daysSincePrevious: 72, isEnd: true },
    ],
    interpretation:
      "The pricing came down in steps, but slowly enough that the market had time to draw its own conclusions. At this level, a relaunch shouldn't chase the price down further — it should reintroduce the apartment to the small group of buyers who'd actually pay for what makes it special.",
  },
  opportunity: {
    headline: "The opportunity is in the reset.",
    body: "Nothing about this penthouse has changed for the worse. What needs to change is the impression it makes in the first ten seconds — the price it opens at, the images buyers see, and the story that frames it. Get those right and the apartment can feel new again.",
    pullQuote:
      "Don't just relist it. Reintroduce it — to the right buyers, on the right terms.",
  },
  strategy: {
    intro:
      "This is how I'd relaunch it. Fewer, better moves — aimed squarely at the buyers who value what this home actually is.",
    stages: [
      {
        key: "reposition",
        number: "01",
        title: "Reposition",
        summary: "Aim it at the buyers a full-floor loft is actually for.",
        points: [
          "Reframe it as a loft penthouse, not a three-bedroom",
          "Set an opening price that draws offers rather than deters them",
          "Build the pitch around scale, light and the Franklin Street address",
          "Identify the handful of active buyers who fit this profile",
        ],
      },
      {
        key: "repackage",
        number: "02",
        title: "Repackage",
        summary: "Present it the way it feels to stand inside it.",
        points: [
          "Reshoot with an architectural photographer, shot for volume and light",
          "Lead with the great room and the ceiling height",
          "A confident, editorial listing narrative",
          "A refined floor plan that shows the flow of a full floor",
          "Consider a short film for qualified buyers",
        ],
      },
      {
        key: "relaunch",
        number: "03",
        title: "Relaunch",
        summary: "Reintroduce it deliberately, to the right rooms first.",
        points: [
          "A private broker preview ahead of any public listing",
          "Coordinated launch across the channels these buyers actually use",
          "Direct outreach to the small circle of agents at this price point",
          "Concentrate attention into a single launch window",
        ],
      },
      {
        key: "re_engage",
        number: "04",
        title: "Re-engage",
        summary: "Return to everyone who circled it the first time.",
        points: [
          "Reconnect with buyers and agents who toured it before",
          "Reach buyers who looked at comparable Tribeca lofts and passed",
          "A disciplined feedback loop through the opening two weeks",
        ],
      },
    ],
  },
  launchPlan: {
    intro:
      "At this level, restraint reads as confidence. The first two weeks should feel deliberate and unhurried — never like a home that's anxious to sell.",
    phases: [
      { label: "Preparation", detail: "Photography, film, floor plan, narrative and pricing all finalized before launch." },
      { label: "Private Preview", detail: "A quiet preview for a trusted circle of agents and their buyers." },
      { label: "Launch", detail: "A single, coordinated public launch across every channel." },
      { label: "Week 1", detail: "By-appointment viewings for qualified buyers while attention peaks." },
      { label: "Week 2 Review", detail: "We review every piece of real feedback and decide the next move together." },
    ],
  },
  recommendation: {
    body: "If this were my listing, I wouldn't drop the price again and hope. I'd take it off the market cleanly, reshoot it to capture how it actually feels, reframe it as the loft penthouse it is, and reintroduce it to the small number of buyers who'd genuinely value it. At this level, precision beats exposure.",
    closing: "I'd welcome the chance to walk you through all of it in person.",
  },
  contact: {
    headline: "Shall we talk it through?",
    body: "I'd be glad to show you exactly what I saw and how I'd approach the relaunch. There's no obligation — but I think you'll find the perspective worth an hour.",
  },
};

export const presentations: PresentationData[] = [
  sellerRelaunchComplete,
  sellerRelaunchPartial,
  sellerRelaunchExtended,
];

export interface DemoEntry {
  id: string;
  label: string;
  description: string;
  data: PresentationData;
}

export const demoEntries: DemoEntry[] = [
  {
    id: "complete",
    label: "Complete data",
    description: "Every field populated — full gallery, listing history and analysis.",
    data: sellerRelaunchComplete,
  },
  {
    id: "partial",
    label: "Partial data",
    description: "Missing sqft, original price, listing history, scheduling link — two images only.",
    data: sellerRelaunchPartial,
  },
  {
    id: "extended",
    label: "Extended content",
    description: "Longer copy, more observations, a different agent and a higher price point.",
    data: sellerRelaunchExtended,
  },
];

export function getPresentationBySlug(slug?: string): PresentationData | undefined {
  if (!slug) return undefined;
  return presentations.find((p) => p.slug === slug);
}
